/**
 * Error Code Registry for Station Explorer
 *
 * Schema: SE-{DOMAIN}-{NNN}
 * Every user-facing error references a code from this registry.
 * Support can search Sentry by code; users can copy it for tickets.
 *
 * UX conventions:
 * - No trailing periods on messages (toast copy style)
 * - Action labels use lowercase "Try again" consistently
 * - Loading/Success notifications separated into STATUS_CODES
 */

// ─── Domain Prefixes ─────────────────────────────────────────────────────────

export const ErrorDomain = {
  AUTH: 'AUTH',
  NET: 'NET',
  API: 'API',
  DATA: 'DATA',
  MAP: 'MAP',
  QC: 'QC',
  AVAIL: 'AVAIL',
  OBS: 'OBS',
  EXP: 'EXP',
  SYS: 'SYS',
} as const;

export type ErrorDomain = typeof ErrorDomain[keyof typeof ErrorDomain];

// ─── Severity Levels ─────────────────────────────────────────────────────────

export const ErrorSeverityLevel = {
  SUCCESS: 'success',
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
  CRITICAL: 'critical',
} as const;

export type ErrorSeverityLevel = typeof ErrorSeverityLevel[keyof typeof ErrorSeverityLevel];

// ─── Error Code Entry ────────────────────────────────────────────────────────

export interface ErrorCodeEntry {
  code: string;
  domain: ErrorDomain;
  severity: ErrorSeverityLevel;
  message: string;
  action?: string;
  actionLabel?: string;
}

// ─── Error Registry (warnings, errors, critical) ─────────────────────────────

export const ERROR_CODES: Record<string, ErrorCodeEntry> = {
  // ── Authentication ──────────────────────────────────────────────────────
  'SE-AUTH-001': {
    code: 'SE-AUTH-001',
    domain: ErrorDomain.AUTH,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Your session has expired',
    action: 'Refresh the page to re-authenticate',
    actionLabel: 'Refresh Page',
  },
  'SE-AUTH-002': {
    code: 'SE-AUTH-002',
    domain: ErrorDomain.AUTH,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Unable to authenticate — service credentials may be misconfigured',
    action: 'Contact support if this persists',
    actionLabel: 'Contact Support',
  },
  'SE-AUTH-003': {
    code: 'SE-AUTH-003',
    domain: ErrorDomain.AUTH,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Authentication is slow — retrying',
  },
  'SE-AUTH-004': {
    code: 'SE-AUTH-004',
    domain: ErrorDomain.AUTH,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Token refresh failed',
    action: 'Refresh the page to continue',
    actionLabel: 'Refresh Page',
  },
  'SE-AUTH-005': {
    code: 'SE-AUTH-005',
    domain: ErrorDomain.AUTH,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Token refresh race condition — concurrent requests failed',
    action: 'Refresh the page',
    actionLabel: 'Refresh Page',
  },

  // ── Network ─────────────────────────────────────────────────────────────
  'SE-NET-001': {
    code: 'SE-NET-001',
    domain: ErrorDomain.NET,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Unable to connect to the server',
    action: 'Check your internet connection and try again',
    actionLabel: 'Try again',
  },
  'SE-NET-002': {
    code: 'SE-NET-002',
    domain: ErrorDomain.NET,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Request timed out — the server may be busy',
    action: 'Try again in a moment',
    actionLabel: 'Try again',
  },
  'SE-NET-003': {
    code: 'SE-NET-003',
    domain: ErrorDomain.NET,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Slow network detected — data may take longer to load',
  },
  'SE-NET-004': {
    code: 'SE-NET-004',
    domain: ErrorDomain.NET,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Server returned an internal error (500)',
    action: 'Try again shortly',
    actionLabel: 'Try again',
  },
  'SE-NET-005': {
    code: 'SE-NET-005',
    domain: ErrorDomain.NET,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Service is restarting (502/503)',
    action: 'Wait 30 seconds and try again',
    actionLabel: 'Try again',
  },
  'SE-NET-006': {
    code: 'SE-NET-006',
    domain: ErrorDomain.NET,
    severity: ErrorSeverityLevel.WARNING,
    message: 'You appear to be offline — showing cached data',
  },
  'SE-NET-007': {
    code: 'SE-NET-007',
    domain: ErrorDomain.NET,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Request aborted — you may have lost connectivity',
    action: 'Check your connection and try again',
    actionLabel: 'Try again',
  },
  'SE-NET-008': {
    code: 'SE-NET-008',
    domain: ErrorDomain.NET,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Slow response from upstream API — partial results shown',
  },

  // ── API ─────────────────────────────────────────────────────────────────
  'SE-API-001': {
    code: 'SE-API-001',
    domain: ErrorDomain.API,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Station not found',
    action: 'Verify the station code and try again',
  },
  'SE-API-002': {
    code: 'SE-API-002',
    domain: ErrorDomain.API,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Server returned unexpected data',
    action: 'Try again or contact support',
    actionLabel: 'Try again',
  },
  'SE-API-003': {
    code: 'SE-API-003',
    domain: ErrorDomain.API,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Service temporarily unavailable',
    action: 'Try again in a few minutes',
    actionLabel: 'Try again',
  },
  'SE-API-004': {
    code: 'SE-API-004',
    domain: ErrorDomain.API,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Too many requests — please slow down',
    action: 'Wait 60 seconds before retrying',
  },
  'SE-API-005': {
    code: 'SE-API-005',
    domain: ErrorDomain.API,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Access denied',
    action: 'Contact support',
    actionLabel: 'Contact Support',
  },
  'SE-API-006': {
    code: 'SE-API-006',
    domain: ErrorDomain.API,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Response was truncated — only partial data returned',
    action: 'Narrow your search for complete results',
  },

  // ── Data ────────────────────────────────────────────────────────────────
  'SE-DATA-001': {
    code: 'SE-DATA-001',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Failed to load station metadata',
    action: 'Try again or clear your browser cache',
    actionLabel: 'Try again',
  },
  'SE-DATA-002': {
    code: 'SE-DATA-002',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Using cached data — server unavailable',
    action: 'Data may be stale — refresh when connectivity returns',
  },
  'SE-DATA-003': {
    code: 'SE-DATA-003',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Data processing failed',
    action: 'Try with fewer stations or a smaller date range',
    actionLabel: 'Try again',
  },
  'SE-DATA-004': {
    code: 'SE-DATA-004',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Invalid response format from server',
    action: 'Report to support with this error code',
    actionLabel: 'Contact Support',
  },
  'SE-DATA-007': {
    code: 'SE-DATA-007',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Cache read failed — loading from server',
  },
  'SE-DATA-008': {
    code: 'SE-DATA-008',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Invalid coordinates — latitude must be -90 to 90, longitude -180 to 180',
    action: 'Correct the values and try again',
  },
  'SE-DATA-009': {
    code: 'SE-DATA-009',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Invalid date range — start date must be before end date',
    action: 'Fix the dates and try again',
  },
  'SE-DATA-010': {
    code: 'SE-DATA-010',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Station code format invalid — expected alphanumeric (3-12 chars)',
  },
  'SE-DATA-011': {
    code: 'SE-DATA-011',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.INFO,
    message: 'Search returned 0 results',
    action: 'Broaden your filters',
  },
  'SE-DATA-012': {
    code: 'SE-DATA-012',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.WARNING,
    message: 'CSV parsing failed — file may be corrupted',
    action: 'Re-download the source file',
  },

  // ── Map & Boundaries ────────────────────────────────────────────────────
  'SE-MAP-001': {
    code: 'SE-MAP-001',
    domain: ErrorDomain.MAP,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Boundary data not available for this region',
  },
  'SE-MAP-002': {
    code: 'SE-MAP-002',
    domain: ErrorDomain.MAP,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Failed to load boundary file',
    action: 'Check your connection and try again',
    actionLabel: 'Try again',
  },
  'SE-MAP-003': {
    code: 'SE-MAP-003',
    domain: ErrorDomain.MAP,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Boundary data is corrupted',
    action: 'Contact support with this error code',
    actionLabel: 'Contact Support',
  },
  'SE-MAP-004': {
    code: 'SE-MAP-004',
    domain: ErrorDomain.MAP,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Map tiles slow to load',
    action: 'Check your connection',
  },
  'SE-MAP-006': {
    code: 'SE-MAP-006',
    domain: ErrorDomain.MAP,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Storage access denied for boundary data',
    action: 'Contact support',
    actionLabel: 'Contact Support',
  },
  'SE-MAP-007': {
    code: 'SE-MAP-007',
    domain: ErrorDomain.MAP,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Map tile server unreachable',
    action: 'Check your connection',
    actionLabel: 'Try again',
  },
  'SE-MAP-008': {
    code: 'SE-MAP-008',
    domain: ErrorDomain.MAP,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Canvas rendering failed — browser may be out of memory',
    action: 'Reduce visible stations or refresh the page',
    actionLabel: 'Refresh Page',
  },
  'SE-MAP-009': {
    code: 'SE-MAP-009',
    domain: ErrorDomain.MAP,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Too many stations to render — showing nearest 5,000',
    action: 'Zoom in or narrow your search',
  },
  'SE-MAP-010': {
    code: 'SE-MAP-010',
    domain: ErrorDomain.MAP,
    severity: ErrorSeverityLevel.WARNING,
    message: 'GeoJSON boundary file oversized — map may be slow',
  },
  'SE-MAP-011': {
    code: 'SE-MAP-011',
    domain: ErrorDomain.MAP,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Leaflet map initialization failed',
    action: 'Refresh the page',
    actionLabel: 'Refresh Page',
  },

  // ── Quality Control ─────────────────────────────────────────────────────
  'SE-QC-001': {
    code: 'SE-QC-001',
    domain: ErrorDomain.QC,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Failed to load quality data',
    action: 'Check your connection and try again',
    actionLabel: 'Try again',
  },
  'SE-QC-002': {
    code: 'SE-QC-002',
    domain: ErrorDomain.QC,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Charts unavailable — data shown in table format',
  },
  'SE-QC-003': {
    code: 'SE-QC-003',
    domain: ErrorDomain.QC,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Quality report generation failed',
    action: 'Try fewer stations or a shorter date range',
    actionLabel: 'Try again',
  },
  'SE-QC-005': {
    code: 'SE-QC-005',
    domain: ErrorDomain.QC,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Heatmap data unavailable for this network',
  },
  'SE-QC-006': {
    code: 'SE-QC-006',
    domain: ErrorDomain.QC,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Sensor heatmap data unavailable for this station',
  },
  'SE-QC-007': {
    code: 'SE-QC-007',
    domain: ErrorDomain.QC,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Quality data may be stale — last updated over 24h ago',
  },

  // ── Availability ────────────────────────────────────────────────────────
  'SE-AVAIL-001': {
    code: 'SE-AVAIL-001',
    domain: ErrorDomain.AVAIL,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Availability search failed',
    action: 'Verify station codes and try again',
    actionLabel: 'Try again',
  },
  'SE-AVAIL-002': {
    code: 'SE-AVAIL-002',
    domain: ErrorDomain.AVAIL,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Some stations had no data for the requested period',
    action: 'Try a different date range',
  },
  'SE-AVAIL-003': {
    code: 'SE-AVAIL-003',
    domain: ErrorDomain.AVAIL,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Too many stations requested',
    action: 'Reduce to 50 stations or fewer',
  },
  'SE-AVAIL-005': {
    code: 'SE-AVAIL-005',
    domain: ErrorDomain.AVAIL,
    severity: ErrorSeverityLevel.ERROR,
    message: 'DuckDB query failed',
    action: 'Try again or reduce your search scope',
    actionLabel: 'Try again',
  },
  'SE-AVAIL-006': {
    code: 'SE-AVAIL-006',
    domain: ErrorDomain.AVAIL,
    severity: ErrorSeverityLevel.CRITICAL,
    message: 'Availability service offline — database initialization failed',
    action: 'Try again in a few minutes',
    actionLabel: 'Try again',
  },
  'SE-AVAIL-007': {
    code: 'SE-AVAIL-007',
    domain: ErrorDomain.AVAIL,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Parquet data file not found',
    action: 'Contact support',
    actionLabel: 'Contact Support',
  },
  'SE-AVAIL-008': {
    code: 'SE-AVAIL-008',
    domain: ErrorDomain.AVAIL,
    severity: ErrorSeverityLevel.INFO,
    message: 'No availability data exists before 2010',
  },
  'SE-AVAIL-009': {
    code: 'SE-AVAIL-009',
    domain: ErrorDomain.AVAIL,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Serving cached availability data (last sync: over 24h)',
  },

  // ── Observations ────────────────────────────────────────────────────────
  'SE-OBS-001': {
    code: 'SE-OBS-001',
    domain: ErrorDomain.OBS,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Failed to load observations',
    action: 'Verify station code and try again',
    actionLabel: 'Try again',
  },
  'SE-OBS-002': {
    code: 'SE-OBS-002',
    domain: ErrorDomain.OBS,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Some parameters have no data for this period',
    action: 'Try a different date range',
  },
  'SE-OBS-003': {
    code: 'SE-OBS-003',
    domain: ErrorDomain.OBS,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Observation data format is invalid',
    action: 'Contact support with this error code',
    actionLabel: 'Contact Support',
  },
  'SE-OBS-005': {
    code: 'SE-OBS-005',
    domain: ErrorDomain.OBS,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Animation data loading failed',
    action: 'Try again or use a shorter time range',
    actionLabel: 'Try again',
  },
  'SE-OBS-006': {
    code: 'SE-OBS-006',
    domain: ErrorDomain.OBS,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Animation buffering — data still loading for this time range',
  },
  'SE-OBS-007': {
    code: 'SE-OBS-007',
    domain: ErrorDomain.OBS,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Observation chart render failed',
    action: 'Try a different parameter or refresh',
    actionLabel: 'Refresh Page',
  },

  // ── Export ──────────────────────────────────────────────────────────────
  'SE-EXP-001': {
    code: 'SE-EXP-001',
    domain: ErrorDomain.EXP,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Export failed',
    action: 'Try a different format or fewer records',
    actionLabel: 'Try again',
  },
  'SE-EXP-002': {
    code: 'SE-EXP-002',
    domain: ErrorDomain.EXP,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Export file is large — download may take time',
  },
  'SE-EXP-003': {
    code: 'SE-EXP-003',
    domain: ErrorDomain.EXP,
    severity: ErrorSeverityLevel.ERROR,
    message: 'PDF generation failed',
    action: 'Try CSV or XLSX instead',
    actionLabel: 'Try again',
  },
  'SE-EXP-005': {
    code: 'SE-EXP-005',
    domain: ErrorDomain.EXP,
    severity: ErrorSeverityLevel.ERROR,
    message: 'XLSX generation failed — dataset too large',
    action: 'Export as CSV instead, or reduce your selection',
    actionLabel: 'Try again',
  },
  'SE-EXP-006': {
    code: 'SE-EXP-006',
    domain: ErrorDomain.EXP,
    severity: ErrorSeverityLevel.ERROR,
    message: 'File download blocked by browser',
    action: 'Check your download settings',
  },
  'SE-EXP-007': {
    code: 'SE-EXP-007',
    domain: ErrorDomain.EXP,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Clipboard write failed — permission denied',
    action: 'Copy manually from the table',
  },

  // ── System ──────────────────────────────────────────────────────────────
  'SE-SYS-001': {
    code: 'SE-SYS-001',
    domain: ErrorDomain.SYS,
    severity: ErrorSeverityLevel.ERROR,
    message: 'Failed to load page module',
    action: 'Check your connection and try again',
    actionLabel: 'Try again',
  },
  'SE-SYS-002': {
    code: 'SE-SYS-002',
    domain: ErrorDomain.SYS,
    severity: ErrorSeverityLevel.CRITICAL,
    message: 'Application encountered an unexpected error',
    action: 'Reload the page',
    actionLabel: 'Reload Page',
  },
  'SE-SYS-003': {
    code: 'SE-SYS-003',
    domain: ErrorDomain.SYS,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Browser storage is full — cached data may be lost',
    action: 'Clear your browser cache',
    actionLabel: 'Clear Cache',
  },
  'SE-SYS-005': {
    code: 'SE-SYS-005',
    domain: ErrorDomain.SYS,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Memory usage high — consider closing unused tabs',
  },
  'SE-SYS-006': {
    code: 'SE-SYS-006',
    domain: ErrorDomain.SYS,
    severity: ErrorSeverityLevel.CRITICAL,
    message: 'Page failed to load — JavaScript chunk unavailable',
    action: 'Hard refresh the page (Ctrl+Shift+R)',
    actionLabel: 'Refresh Page',
  },
  'SE-SYS-007': {
    code: 'SE-SYS-007',
    domain: ErrorDomain.SYS,
    severity: ErrorSeverityLevel.ERROR,
    message: 'IndexedDB access denied by browser',
    action: 'Enable site data in privacy settings',
  },
  'SE-SYS-008': {
    code: 'SE-SYS-008',
    domain: ErrorDomain.SYS,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Background tab — requests may time out',
    action: 'Return to this tab to resume',
  },
  'SE-SYS-009': {
    code: 'SE-SYS-009',
    domain: ErrorDomain.SYS,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Geolocation permission denied',
    action: 'Enable location access in browser settings',
  },
  'SE-SYS-010': {
    code: 'SE-SYS-010',
    domain: ErrorDomain.SYS,
    severity: ErrorSeverityLevel.INFO,
    message: 'Application updated in background',
    action: 'Refresh to load the latest version',
    actionLabel: 'Refresh',
  },
};

// ─── Status Codes (info/success notifications — not errors) ──────────────────
// Separated from ERROR_CODES to keep the error registry focused on failures.

export const STATUS_CODES: Record<string, ErrorCodeEntry> = {
  'SE-DATA-005': {
    code: 'SE-DATA-005',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.INFO,
    message: 'Loading station data',
  },
  'SE-DATA-006': {
    code: 'SE-DATA-006',
    domain: ErrorDomain.DATA,
    severity: ErrorSeverityLevel.SUCCESS,
    message: 'Station data loaded successfully',
  },
  'SE-MAP-005': {
    code: 'SE-MAP-005',
    domain: ErrorDomain.MAP,
    severity: ErrorSeverityLevel.INFO,
    message: 'Loading boundaries for selected region',
  },
  'SE-QC-004': {
    code: 'SE-QC-004',
    domain: ErrorDomain.QC,
    severity: ErrorSeverityLevel.INFO,
    message: 'Processing quality metrics',
  },
  'SE-QC-008': {
    code: 'SE-QC-008',
    domain: ErrorDomain.QC,
    severity: ErrorSeverityLevel.INFO,
    message: 'No QC failures detected for this period',
  },
  'SE-AVAIL-004': {
    code: 'SE-AVAIL-004',
    domain: ErrorDomain.AVAIL,
    severity: ErrorSeverityLevel.INFO,
    message: 'Searching availability data',
  },
  'SE-OBS-004': {
    code: 'SE-OBS-004',
    domain: ErrorDomain.OBS,
    severity: ErrorSeverityLevel.SUCCESS,
    message: 'Observations loaded',
  },
  'SE-OBS-008': {
    code: 'SE-OBS-008',
    domain: ErrorDomain.OBS,
    severity: ErrorSeverityLevel.WARNING,
    message: 'Observation animation reached end of available data',
  },
  'SE-EXP-004': {
    code: 'SE-EXP-004',
    domain: ErrorDomain.EXP,
    severity: ErrorSeverityLevel.SUCCESS,
    message: 'Export ready — downloading',
  },
  'SE-SYS-004': {
    code: 'SE-SYS-004',
    domain: ErrorDomain.SYS,
    severity: ErrorSeverityLevel.INFO,
    message: 'A new version is available',
    action: 'Refresh to update',
    actionLabel: 'Refresh',
  },
};

// ─── Combined lookup (errors + status) ───────────────────────────────────────

const ALL_CODES: Record<string, ErrorCodeEntry> = { ...ERROR_CODES, ...STATUS_CODES };

// ─── Lookup Helpers ──────────────────────────────────────────────────────────

/**
 * Get an error code entry from either registry. Returns undefined if not found.
 */
export function getErrorCode(code: string): ErrorCodeEntry | undefined {
  return ALL_CODES[code];
}

/**
 * Get user-facing message for a code. Falls back to a generic message.
 */
export function getErrorMessage(code: string): string {
  return ALL_CODES[code]?.message ?? 'An unexpected error occurred — please try again';
}

/**
 * Map an HTTP status code + domain to the most relevant error code.
 * Distinguishes 401 (session expired) from 403 (access denied/CORS).
 * ponytail: Simple lookup — covers common cases. Upgrade if edge cases grow.
 */
export function mapHttpToErrorCode(status: number, domain: ErrorDomain = ErrorDomain.API): string {
  if (status === 401) return 'SE-AUTH-001';
  if (status === 403) return 'SE-API-005'; // Access denied (CORS, permissions)
  if (status === 404 && domain === ErrorDomain.API) return 'SE-API-001';
  if (status === 404) return `SE-${domain}-001`;
  if (status === 429) return 'SE-API-004';
  if (status === 500) return 'SE-NET-004';
  if (status === 502 || status === 503) return 'SE-NET-005';
  if (status === 504) return 'SE-NET-002';
  return `SE-${domain}-001`;
}

/**
 * Generate a correlation ID for client-side errors (when no server requestId exists).
 * Format: 8-char hex for brevity in user-facing copy.
 */
export function generateCorrelationId(): string {
  return Math.random().toString(16).slice(2, 10);
}
